import { FIELD_GRID_CLASS, FIELD_LABEL_CLASS, GridCells, Row, SectionCard } from 'components/GridItemList';
import Loader from 'components/Loader';
import { FormattedDate } from 'components/misc/FormattedDate';
import { FormattedMessage } from 'react-intl';
import { useGetEmployeeWebQuery } from 'store/api/employeeWeb';
import { ClassifierData } from 'types/classifiers';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { WEB_TITLE_FIELDS } from './useWebIntegrationRows';
import { WebPictureViewer } from './WebPictureInput';

interface Props {
    employeeId: string;
    classifiers: Record<string, ClassifierData>;
}

/**
 * Everything the website receives about the employee, as an overview: the flags and the picture are edited on the
 * General tab's Web integration card, and the rest is the employee's own data or derived from it.
 */
export function WebView({ employeeId, classifiers }: Props) {
    const { data: detail, isLoading } = useGetEmployeeWebQuery(employeeId);

    if (isLoading) {
        return <Loader />;
    }
    if (!detail) {
        return null;
    }
    const yesNo = (value: boolean) => t(value ? 'yes' : 'no');
    // In the classifier's order, as the employee's own field lists them.
    const languages = getOptionsFromClassifier<string, string>(classifiers['LANGUAGE'])
        .filter((option) => detail.languagesSpoken.includes(option.value))
        .map((option) => option.label);
    const rows: Row[] = [
        { label: t('employees.web.user-id'), content: String(detail.employeeId) },
        { label: t('employees.web.name'), content: detail.fullName || '-' },
        ...WEB_TITLE_FIELDS.map(([name, label]) => ({ label: t(label), content: detail[name] || '-' })),
        { label: t('employees.web.mobile-phone'), content: detail.mobilePhone || '-' },
        { label: t('employees.web.publicize-mobile'), content: yesNo(detail.publicizeMobile) },
        { label: t('employees.web.local-phone'), content: detail.localPhone || '-' },
        { label: t('employees.web.email'), content: detail.workEmailAddress || '-' },
        {
            label: t('employees.web.country'),
            content: detail.countryCode ? getClassifierText(classifiers['COUNTRY'], detail.countryCode) : '-',
        },
        { label: t('employees.web.languages-spoken'), content: languages.join(', ') || '-' },
        { label: t('employees.web.publicize-person'), content: yesNo(detail.publicizePerson) },
        { label: t('employees.web.is-business-service'), content: yesNo(detail.isBusinessService) },
        { label: t('employees.web.on-leave'), content: yesNo(detail.onLeave) },
        {
            label: t('employees.web.last-published'),
            content: <FormattedDate value={detail.lastPublishedAt} withTime />,
        },
    ];
    return (
        <SectionCard title={<FormattedMessage id="employees.web.profile" />}>
            <div className={FIELD_GRID_CLASS}>
                <dl className="col-span-full m-0 py-2">
                    <dt className={FIELD_LABEL_CLASS}>{t('employees.web.picture')}</dt>
                    <dd className="m-0 mt-1">
                        <WebPictureViewer pictureBase64={detail.pictureBase64} fullName={detail.fullName} />
                    </dd>
                </dl>
                <dl className="contents">
                    <GridCells rows={rows} />
                </dl>
            </div>
        </SectionCard>
    );
}
